// Field placement config for the two government PAN form overlays.
//
// Coordinates are PERCENTAGES of the A4 page (which matches the background
// image aspect ratio), so they scale identically in the on-screen preview and
// in the Puppeteer-rendered A4 PDF. Values were measured by pixel-scanning the
// blank form images for box borders. Tune `top` / `left` / `pitch` here to
// align the overlaid text with the printed boxes — no other file needs to
// change. For grid fields: `left` is the LEFT edge of the first box and `pitch`
// is the box-to-box step (both %); each character is centered in its box.
//
// IMPORTANT: the name/address rows have 25 boxes spanning the measured grid, so
// `pitch` must be the exact measured step — an over-estimate makes long lines
// (e.g. full addresses) drift right and spill past the last box. `maxChars`
// must also match the real box count (NAME_MAX): rendering more characters than
// there are boxes draws them past the grid's last box, against the form edge.

export interface PanFormValues {
  firstName: string;
  middleName: string;
  lastName: string;
  aadhaarName: string; // Name as per Aadhaar (single line)
  // ---------------------------------------------------------------------------
  // CAPTURE-ONLY fields (spec / vendor upload file).
  //
  // Form No. 93 and the Correction form have NO printed box for any of these —
  // they belong to the NSDL data-entry screen and the vendor's upload sheet. We
  // collect them in step 1 and keep them in the stored `formData` so the vendor
  // file can be produced from the record; they are deliberately absent from
  // NEW_FORM_FIELDS / CORRECTION_FORM_FIELDS and never print.
  // ---------------------------------------------------------------------------
  applicantStatus: string; // P / H / C / F / E / T / A / B / L / J / G
  countryOfCitizenship: string; // country code, e.g. "IN"
  acknowledgementNumber: string;
  registrationNo: string;
  aadhaarEid: string; // Aadhaar Enrolment ID, used when no Aadhaar number
  hasOtherName: "YES" | "NO" | "";
  otherFirstName: string;
  otherMiddleName: string;
  otherLastName: string;
  physicalPanCard: "YES" | "NO" | ""; // "wishes to have a physical PAN card"
  dateOfReceipt: string; // DD/MM/YYYY
  dateOfVerification: string; // DD/MM/YYYY
  photoPresent: "YES" | "NO" | "";
  signaturePresent: "YES" | "NO" | "";

  // ---------------------------------------------------------------------------
  // CODE fields. The printed form carries the plain name or a tick, while the
  // vendor file needs the master code — so a dropdown writes BOTH: the code
  // here, and the human-readable text into the matching printed field (e.g.
  // stateCode -> "27", state -> "RAJASTHAN"). The template stays untouched.
  // ---------------------------------------------------------------------------
  stateCode: string;
  countryCode: string;
  officeStateCode: string;
  officeCountryCode: string;
  raStateCode: string;
  raCountryCode: string;
  /** Document code masters — also drive the printed proof tick boxes. */
  poiCode: string;
  poaCode: string;
  podbCode: string;
  raPoiCode: string;
  raPoaCode: string;
  /** Capacity of verifier — numeric(2); prints as `declarantCapacity` text. */
  verifierCapacityCode: string;
  gender: "MALE" | "FEMALE" | "TRANSGENDER" | "";
  dob: string; // digits only, DDMMYYYY
  aadhaar: string; // 12 digits
  pan: string; // 10 chars (correction form only)
  flat: string;
  road: string;
  postOffice: string;
  area: string;
  district: string;
  state: string;
  country: string;
  pincode: string;
  // NEW form section 6 — Office Address (separate from the residence address).
  officeFlat: string;
  officeRoad: string;
  officePostOffice: string;
  officeArea: string;
  officeDistrict: string;
  officeState: string;
  officeCountry: string;
  officePincode: string;
  mobileCountryCode: string;
  mobile: string;
  email: string;
  // NEW form section 10(iii) — Landline (STD code + number).
  landlineStd: string;
  landline: string;
  // NEW form section 7 — Residential Status (select one).
  residentialStatus: "RESIDENT" | "NON_RESIDENT" | "NOT_ORDINARY" | "";
  // NEW form section 8 — Passport Number.
  passport: string;
  // NEW form section 9 — Taxpayer Identification Number (TIN).
  tin: string;
  fatherFirst: string;
  fatherMiddle: string;
  fatherLast: string;
  motherFirst: string;
  motherMiddle: string;
  motherLast: string;
  // PART B — Source of Income (Q11): 0+ selected keys. The application record
  // has no income data, so this is operator-selected in the Edit panel.
  incomeSources: IncomeSource[];
  // PART C — Q12 "Whether mother/father is a single parent?" (Yes/No).
  // Prefilled from the record's isSingleParent flag.
  singleParent: "YES" | "NO" | "";
  // CORRECTION form Q10 "Name of parent to be printed on PAN card".
  // Prefilled from the record's parentType.
  printParent: "FATHER" | "MOTHER" | "";
  // CORRECTION form address "select one" — Residence / Office. The app only
  // captures a residence address, so this defaults to RESIDENCE; editable.
  addressType: "RESIDENCE" | "OFFICE" | "";

  // ---------------------------------------------------------------------------
  // PAGE 2 — Verification & Declaration (both forms) + NEW-form Parts D–G.
  // The application record has no source for any of these, so they are filled
  // in the Edit panel. Text fields render blank until the operator types them.
  // ---------------------------------------------------------------------------
  // "a. I, <declarantName>, in the capacity of <declarantCapacity> ..." and the
  // "Name:" line at the foot of the NEW form share `declarantName`.
  declarantName: string;
  declarantCapacity: string; // "Self" / "Representative Assessee"
  place: string;
  date: string; // free text (DD/MM/YYYY) — prefilled with today
  designation: string; // NEW form foot only
  // Signature / thumb-impression image, pasted or uploaded then cropped in the
  // Edit panel. Stored as a base64 data-URL so it embeds directly in the posted
  // HTML (Puppeteer prints it with no upload/storage plumbing).
  signature: string;
  // Passport photograph for the form's "Recent colour photograph (4.5 cm x
  // 3.5 cm)" box. Prefilled from the record's passportPhotoUrl, then optionally
  // cropped/rotated in the Edit panel. Like `signature`, it is held as a
  // base64 data-URL and stripped before persisting, so the applicant's own
  // uploaded file is never modified.
  photo: string;
  /**
   * Print the signature across the LEFT photo — the box Form No. 93 captions
   * "with Sign/Left thumb impression across the photo". "YES" by default when
   * the application has both a photo and a signature.
   */
  crossSign: "YES" | "NO" | "";
  /**
   * Operator's adjustment of the cross sign, on top of the field's placement:
   * shift in % of page width / height, size in % of the default, tilt in
   * degrees added to the field's own rotation. Held as strings like every
   * other value (setField is string-typed); parsed by the template.
   */
  crossSignDx: string;
  crossSignDy: string;
  crossSignSize: string;
  crossSignTilt: string;
  /**
   * The same adjustment for the signature in the Verification & Declaration
   * box: shift in % of the page, size in % of the default, tilt in degrees.
   */
  sigDx: string;
  sigDy: string;
  sigSize: string;
  sigTilt: string;
  /**
   * Put the Tax Invoice cum Acknowledgement receipt, cut out of the
   * acknowledgement PDF, as the first page. The image itself is held by the
   * dialog, never in these values, so it is not persisted with formData.
   */
  receiptPage: "YES" | "NO" | "";

  // NEW form Part G — Documents submitted (§23 applicant, §24 rep. assessee).
  proofOfIdentity: boolean;
  proofOfAddress: boolean;
  proofOfDob: boolean;
  raProofOfIdentity: boolean;
  raProofOfAddress: boolean;
  // NEW form Part F §22 — Address for Communication (select one).
  addressForComm: "RESIDENCE" | "RA" | "OFFICE" | "";

  // CORRECTION form Part C §11 — Documents submitted as proof.
  corrProofOfIdentity: boolean;
  corrProofOfAddress: boolean;
  corrProofOfDob: boolean;
  corrDocProof: boolean; // (iv) Documentary proof for other changes
  corrCopyOfPan: boolean; // (v) Copy of PAN

  // CORRECTION form — left "Tick Box" column (Sr. No. 1–9): which fields are
  // being changed/corrected. Sr. 1/3/7/8 (Name / DOB / Contact / Father) are
  // ticked by default; the operator can toggle any in the Edit panel.
  changeName: boolean; // Sr. 1
  changeGender: boolean; // Sr. 2
  changeDob: boolean; // Sr. 3
  changeAddress: boolean; // Sr. 4
  changePassport: boolean; // Sr. 5
  changeTin: boolean; // Sr. 6
  changeContact: boolean; // Sr. 7
  changeFather: boolean; // Sr. 8
  changeMother: boolean; // Sr. 9

  // NEW form Part D — Assessing Officer (AO) Code. Blank/editable.
  aoAreaCode: string;
  aoType: string;
  aoRangeCode: string;
  aoNo: string;
  // NEW form Part E — Representative Assessee (name / PAN / Aadhaar). Blank.
  raFirst: string;
  raMiddle: string;
  raLast: string;
  raPan: string;
  raAadhaar: string;
  // NEW form §20 — Representative Assessee address. Blank/editable.
  raFlat: string;
  raRoad: string;
  raPostOffice: string;
  raArea: string;
  raDistrict: string;
  raState: string;
  raCountry: string;
  raPincode: string;
  // NEW form §21 — Representative Assessee contact. Blank/editable.
  raMobileCC: string;
  raMobile: string;
  raEmail: string;
  raLandlineStd: string;
  raLandline: string;
}

export type IncomeSource =
  | "SALARY"
  | "BUSINESS"
  | "HOUSE_PROPERTY"
  | "CAPITAL_GAINS"
  | "OTHER_SOURCES"
  | "NO_INCOME";

// Labels match the printed form wording; order matches the two printed rows.
export const INCOME_SOURCE_OPTIONS: { key: IncomeSource; label: string }[] = [
  { key: "SALARY", label: "Salary" },
  { key: "BUSINESS", label: "Income from Business/Profession" },
  { key: "HOUSE_PROPERTY", label: "Income from House Property" },
  { key: "CAPITAL_GAINS", label: "Capital Gains" },
  { key: "OTHER_SOURCES", label: "Income from Other Sources" },
  { key: "NO_INCOME", label: "No Income" },
];

export const emptyPanFormValues: PanFormValues = {
  firstName: "", middleName: "", lastName: "", aadhaarName: "",
  // capture-only (spec / vendor file) — never printed
  applicantStatus: "", countryOfCitizenship: "", acknowledgementNumber: "",
  registrationNo: "", aadhaarEid: "", hasOtherName: "",
  otherFirstName: "", otherMiddleName: "", otherLastName: "",
  physicalPanCard: "", dateOfReceipt: "", dateOfVerification: "",
  photoPresent: "", signaturePresent: "",
  // code fields (dropdowns also write the printed text field)
  stateCode: "", countryCode: "", officeStateCode: "", officeCountryCode: "",
  raStateCode: "", raCountryCode: "",
  poiCode: "", poaCode: "", podbCode: "", raPoiCode: "", raPoaCode: "",
  verifierCapacityCode: "",
  gender: "", dob: "", aadhaar: "", pan: "",
  flat: "", road: "", postOffice: "", area: "", district: "",
  state: "", country: "", pincode: "",
  officeFlat: "", officeRoad: "", officePostOffice: "", officeArea: "", officeDistrict: "",
  officeState: "", officeCountry: "", officePincode: "",
  mobileCountryCode: "", mobile: "", email: "",
  landlineStd: "", landline: "",
  residentialStatus: "", passport: "", tin: "",
  fatherFirst: "", fatherMiddle: "", fatherLast: "",
  motherFirst: "", motherMiddle: "", motherLast: "",
  incomeSources: [], singleParent: "", printParent: "", addressType: "",
  // Page 2 — Verification & Declaration
  declarantName: "", declarantCapacity: "", place: "", date: "", designation: "",
  signature: "", photo: "", crossSign: "", receiptPage: "",
  crossSignDx: "0", crossSignDy: "0", crossSignSize: "100", crossSignTilt: "0",
  sigDx: "0", sigDy: "0", sigSize: "100", sigTilt: "0",
  // NEW form Part G proofs + §22 communication address
  proofOfIdentity: false, proofOfAddress: false, proofOfDob: false,
  raProofOfIdentity: false, raProofOfAddress: false, addressForComm: "",
  // CORRECTION Part C §11 proofs
  corrProofOfIdentity: false, corrProofOfAddress: false, corrProofOfDob: false,
  corrDocProof: false, corrCopyOfPan: false,
  // CORRECTION change-ticks (Sr. 1/3/7/8 default on — set in PanFormDialog init)
  changeName: false, changeGender: false, changeDob: false, changeAddress: false,
  changePassport: false, changeTin: false, changeContact: false,
  changeFather: false, changeMother: false,
  // NEW form Part D/E — AO code + Representative Assessee (all blank/editable)
  aoAreaCode: "", aoType: "", aoRangeCode: "", aoNo: "",
  raFirst: "", raMiddle: "", raLast: "", raPan: "", raAadhaar: "",
  raFlat: "", raRoad: "", raPostOffice: "", raArea: "", raDistrict: "",
  raState: "", raCountry: "", raPincode: "",
  raMobileCC: "", raMobile: "", raEmail: "", raLandlineStd: "", raLandline: "",
};

/**
 * A document already uploaded on the PAN application that the operator can
 * append to the generated PDF as its own A4 page.
 *
 * `url` always points at the applicant's original file and is never rewritten.
 * `edited` holds an optional cropped/rotated data-URL used *only* for printing,
 * so attaching and adjusting a document here cannot modify the application.
 */
export type PanFormAttachment = {
  id: string; // stable key — the record field name, or `other-<index>`
  label: string;
  url: string;
  selected: boolean;
  edited?: string; // data-URL, set only if the operator cropped/rotated it
};

/**
 * How many documents share one appended A4 page, and how they are arranged.
 * "1-up" reproduces the original behaviour (one document per page).
 */
export type AttachmentLayout = "1-up" | "2-up" | "3-up" | "2x2";

export const ATTACHMENT_LAYOUTS: { key: AttachmentLayout; label: string; slots: number }[] = [
  { key: "1-up", label: "1 per page", slots: 1 },
  { key: "2-up", label: "2 per page (stacked)", slots: 2 },
  { key: "3-up", label: "3 per page (stacked)", slots: 3 },
  { key: "2x2", label: "4 per page (2 x 2)", slots: 4 },
];

export const slotsForLayout = (layout: AttachmentLayout) =>
  ATTACHMENT_LAYOUTS.find((l) => l.key === layout)?.slots || 1;

/**
 * One appended A4 page holding one or more documents. `items` holds attachment
 * ids; the images themselves stay in the attachment list, so cropping a
 * document updates every page it appears on.
 */
export type PanFormAttachmentPage = {
  id: string;
  layout: AttachmentLayout;
  items: string[]; // attachment ids, at most slotsForLayout(layout)
};

// `page` selects which background page the field is overlaid on (defaults to 1
// when omitted). Coordinates are always relative to their OWN page.
export type PanFormField =
  | {
      kind: "grid";
      key: keyof PanFormValues;
      top: number;
      left: number;
      pitch: number; // % step between box centers
      maxChars?: number;
      slice?: [number, number]; // render only value.slice(a,b) — used for DOB groups
      /**
       * Multi-row grid. `lines` is how many printed rows the value may occupy
       * and `line` is which of them THIS field draws — the value is wrapped on
       * word boundaries at `maxChars` and only that row is rendered. Used for
       * the three-row "Name (as per Aadhaar)" block, whose rows are separate
       * grids at different `top`s. Omit both for an ordinary single-row grid.
       */
      line?: number;
      lines?: number;
      fontSize?: number;
      page?: 1 | 2;
    }
  | {
      kind: "text";
      key: keyof PanFormValues;
      top: number;
      left: number;
      width?: number;
      fontSize?: number;
      uppercase?: boolean;
      align?: "left" | "center" | "right";
      /**
       * Draw a dotted rule along the value's baseline, so the value sits ON the
       * line the way it does on a hand-filled form. `ruleTop` is the baseline
       * itself as a % of page height (use `declRuleTop`), NOT the text box's
       * top — the two differ by the baseline drop. The rule spans `width`.
       *
       * Used where the printed form has a short dotted leader that a typed
       * value would otherwise start AFTER, leaving "Place……… demo" instead of
       * the value written over the dots.
       */
      rule?: boolean;
      ruleTop?: number;
      page?: 1 | 2;
    }
  | {
      // Tick glyph. UNLIKE the other kinds, `top` / `left` are the CENTRE of
      // the printed tick box, not its top-left — the template centres the glyph
      // on that point. Every value below was measured by pixel-scanning the
      // blank form for the box's four borders, so a tick can never sit outside
      // its box.
      kind: "tick";
      top: number;
      left: number;
      on: (v: PanFormValues) => boolean;
      fontSize?: number;
      page?: 1 | 2;
    }
  | {
      // Signature / thumb-impression image. `key` holds a base64 data-URL.
      kind: "image";
      key: keyof PanFormValues;
      top: number;
      left: number;
      width: number; // % of page width
      height: number; // % of page height
      page?: 1 | 2;
    }
  | {
      // Signature signed ACROSS the left photo. Unlike "image" it is not
      // clipped to a box: it is placed over the photo's right edge, so it lands
      // half on the photo and half on the form, the way it is signed by hand.
      // Drawn only while `crossSign` is "YES".
      kind: "crossSign";
      key: keyof PanFormValues;
      top: number;
      left: number;
      width: number; // % of page width
      height: number; // % of page height
      rotate?: number; // degrees, negative tilts it up to the right
      page?: 1 | 2;
    };

/**
 * Spread a value across the rows of a multi-row box grid, one character per box,
 * continuously — row 1 takes the first `width` characters, row 2 the next, and so
 * on. Spaces between words occupy a box of their own, exactly as these forms are
 * filled by hand.
 *
 * It does NOT wrap on word boundaries. Breaking on words left the first row half
 * empty and pushed the rest of the name down early, which is not how the printed
 * grid reads: the boxes are one continuous run, so a name simply carries on into
 * the next row.
 */
export const sliceToBoxes = (value: string, width: number, lines: number): string[] => {
  const text = (value || "").trim().replace(/\s+/g, " ");
  const rows: string[] = [];
  for (let i = 0; i < lines; i += 1) rows.push(text.slice(i * width, (i + 1) * width));
  return rows;
};

// Measured box pitch for the 25-box name/address rows.
const NEW_PITCH = 2.574; // ~(94.68 - 30.27) / 25 ; last border at 97.0% is the form edge
const CORR_PITCH = 2.6; // ~(94.59 - 29.59) / 25
const NAME_MAX = 25;
/** Printed rows in the "Name (as per Aadhaar)" block on both forms. */
export const AADHAAR_NAME_ROWS = 3;
/** Characters the Aadhaar-name input accepts — the whole block. */
export const AADHAAR_NAME_MAX = NAME_MAX * AADHAAR_NAME_ROWS;

// --- Verification & Declaration: baseline → `top` -------------------------
//
// Both background pages are 1034 x 1462 px, and every y quoted in the
// declaration block below is a baseline read straight off those pixels.
//
// A text field's `top`, though, is the TOP of its line box, so it has to be
// walked back from the baseline. The line box is `line-height: 1` (= fontSize
// CSS px) and the glyphs sit on the baseline at
//   half-leading + ascent = (1 − 1.117)/2 + 0.905 ≈ 0.846 em
// for Arial / Liberation Sans (metric-compatible, which is what the server's
// headless Chrome falls back to). The A4 page renders 1122.5 CSS px tall
// against a 1462 px background, so one CSS px is 1462/1122.5 = 1.302
// background px:
//   drop = 0.846 × 10px × 1.302 ≈ 10.5 background px
// This was verified by rendering the overlay through the same headless Chrome
// that generates the PDF and pixel-comparing the typed value's baseline with
// the printed label's — they land on the same row.
//
// The earlier values subtracted 8.5 BACKGROUND px instead of 8.5 CSS px, which
// printed every entry in this block ~2.5px below its rule.
const DECL_BASELINE_DROP = 10.5; // background px, at fontSize 10
const PAGE_H = 1462; // background page height, px

/** `top` (% of page height) that puts a fontSize-10 baseline on background y. */
const declTop = (baselineY: number) =>
  Math.round(((baselineY - DECL_BASELINE_DROP) / PAGE_H) * 10000) / 100;

/** `top` (% of page height) of the baseline ITSELF — where a rule is drawn. */
const declRuleTop = (baselineY: number) =>
  Math.round((baselineY / PAGE_H) * 10000) / 100;

// ---------------------------------------------------------------------------
// NEW form — FORM NO. 93 (Application for Allotment of PAN). Grid left = 30.27
// ---------------------------------------------------------------------------
export const NEW_FORM_FIELDS: PanFormField[] = [
  // The form has TWO photo boxes at the top, both 3.5 x 4.5 cm; the same
  // photograph is affixed in each. Measured off the page-1 background:
  //   left  box: x 79–251  (7.64%–24.28%), y 40–261 (2.74%–17.85%)
  //   right box: x 769–942 (74.37%–91.10%), y 40–262 (2.74%–17.92%)
  // Both are 172 x 221 px at 125 DPI = 3.51 x 4.51 cm, matching the printed size.
  // The left box is the one captioned "with Sign/Left thumb impression across
  // the photo of the applicant".
  { kind: "image", key: "photo", top: 2.74, left: 7.64, width: 16.63, height: 15.12 },
  { kind: "image", key: "photo", top: 2.74, left: 74.37, width: 16.73, height: 15.18 },
  // Signature across the left photo: over its lower half and out past its right
  // edge (24.27%). Listed after the photos so it draws on top of them.
  { kind: "crossSign", key: "signature", top: 11, left: 15.5, width: 15, height: 5.5, rotate: -6 },
  { kind: "grid", key: "firstName", top: 23.3, left: 30.27, pitch: NEW_PITCH, maxChars: NAME_MAX },
  { kind: "grid", key: "middleName", top: 24.85, left: 30.27, pitch: NEW_PITCH, maxChars: NAME_MAX },
  { kind: "grid", key: "lastName", top: 26.5, left: 30.27, pitch: NEW_PITCH, maxChars: NAME_MAX },
  // "B. Name (as per Aadhaar)" is a THREE-row block, not one row. Row interiors
  // measured off the page-1 background (1034 x 1462): y439-459, y462-482,
  // y485-505 — i.e. row centres 30.71 / 32.28 / 33.86%, less the calibrated
  // 0.52% glyph half-height the other grid rows use. Same left and pitch as the
  // First/Middle/Last rows above (pixel-verified), so only `top` differs.
  { kind: "grid", key: "aadhaarName", top: 30.2, left: 30.27, pitch: NEW_PITCH, maxChars: NAME_MAX, line: 0, lines: AADHAAR_NAME_ROWS },
  { kind: "grid", key: "aadhaarName", top: 31.76, left: 30.27, pitch: NEW_PITCH, maxChars: NAME_MAX, line: 1, lines: AADHAAR_NAME_ROWS },
  { kind: "grid", key: "aadhaarName", top: 33.34, left: 30.27, pitch: NEW_PITCH, maxChars: NAME_MAX, line: 2, lines: AADHAAR_NAME_ROWS },
  // Gender ticks — box centres (x313-341 / 393-421 / 499-527, y508-528)
  { kind: "tick", top: 35.47, left: 31.67, on: (v) => v.gender === "MALE" },
  { kind: "tick", top: 35.47, left: 39.41, on: (v) => v.gender === "FEMALE" },
  { kind: "tick", top: 35.47, left: 49.66, on: (v) => v.gender === "TRANSGENDER" },
  // Date of Birth: dd | mm | yyyy
  { kind: "grid", key: "dob", top: 36.6, left: 30.27, pitch: 2.6, slice: [0, 2] },
  { kind: "grid", key: "dob", top: 36.6, left: 38.01, pitch: 2.6, slice: [2, 4] },
  { kind: "grid", key: "dob", top: 36.6, left: 45.74, pitch: 2.52, slice: [4, 8] },
  // Aadhaar
  { kind: "grid", key: "aadhaar", top: 38.1, left: 30.27, pitch: 2.52, maxChars: 12 },
  // Residence Address
  { kind: "grid", key: "flat", top: 41.75, left: 30.27, pitch: NEW_PITCH, maxChars: NAME_MAX },
  { kind: "grid", key: "road", top: 43.3, left: 30.27, pitch: NEW_PITCH, maxChars: NAME_MAX },
  { kind: "grid", key: "postOffice", top: 44.95, left: 30.27, pitch: NEW_PITCH, maxChars: NAME_MAX },
  { kind: "grid", key: "area", top: 46.5, left: 30.27, pitch: NEW_PITCH, maxChars: NAME_MAX },
  { kind: "grid", key: "district", top: 48.1, left: 30.27, pitch: NEW_PITCH, maxChars: NAME_MAX },
  { kind: "text", key: "state", top: 49.85, left: 22.5, width: 13.5, fontSize: 9, uppercase: true, align: "center" },
  { kind: "text", key: "country", top: 49.85, left: 48.75, width: 15.75, fontSize: 10, uppercase: true, align: "center" },
  { kind: "grid", key: "pincode", top: 49.85, left: 79.3, pitch: 2.58, maxChars: 6 },
  // Section 6 — Office Address (same grid geometry as residence, shifted down)
  { kind: "grid", key: "officeFlat", top: 53.3, left: 30.27, pitch: NEW_PITCH, maxChars: NAME_MAX },
  { kind: "grid", key: "officeRoad", top: 54.86, left: 30.27, pitch: NEW_PITCH, maxChars: NAME_MAX },
  { kind: "grid", key: "officePostOffice", top: 56.51, left: 30.27, pitch: NEW_PITCH, maxChars: NAME_MAX },
  { kind: "grid", key: "officeArea", top: 58.06, left: 30.27, pitch: NEW_PITCH, maxChars: NAME_MAX },
  { kind: "grid", key: "officeDistrict", top: 59.66, left: 30.27, pitch: NEW_PITCH, maxChars: NAME_MAX },
  { kind: "text", key: "officeState", top: 61.4, left: 22.5, width: 13.5, fontSize: 9, uppercase: true, align: "center" },
  { kind: "text", key: "officeCountry", top: 61.4, left: 48.75, width: 15.75, fontSize: 10, uppercase: true, align: "center" },
  { kind: "grid", key: "officePincode", top: 61.4, left: 79.3, pitch: 2.58, maxChars: 6 },
  // Section 7 — Residential Status (select one). Boxes y923-943.
  { kind: "tick", top: 63.85, left: 36.65, on: (v) => v.residentialStatus === "RESIDENT" },
  { kind: "tick", top: 63.85, left: 47.05, on: (v) => v.residentialStatus === "NON_RESIDENT" },
  { kind: "tick", top: 63.85, left: 60.06, on: (v) => v.residentialStatus === "NOT_ORDINARY" },
  // Section 8 — Passport Number
  { kind: "grid", key: "passport", top: 65.7, left: 55.13, pitch: 2.51, maxChars: 12 },
  // Section 9 — Taxpayer Identification Number (TIN)
  { kind: "grid", key: "tin", top: 68.0, left: 45.26, pitch: 2.51, maxChars: 20 },
  // Contact
  { kind: "grid", key: "mobileCountryCode", top: 71.6, left: 40.3, pitch: 2.55, maxChars: 3 },
  { kind: "grid", key: "mobile", top: 71.6, left: 63.25, pitch: 2.48, maxChars: 10 },
  // Email box measured at x416-983 (40.23%-95.07%), y1069-1089: start just
  // inside the left border so a long address still fits on the line.
  { kind: "text", key: "email", top: 73.47, left: 41.5, width: 53, fontSize: 10 },
  // Section 10(iii) — Landline (STD code + number)
  { kind: "grid", key: "landlineStd", top: 74.8, left: 40.33, pitch: 2.46, maxChars: 5 },
  { kind: "grid", key: "landline", top: 74.8, left: 63.35, pitch: 2.51, maxChars: 8 },
  // PART B — Source of Income (Q11). Two rows of three tick boxes
  // (y1161-1181 and y1189-1209).
  { kind: "tick", top: 80.13, left: 31.38, on: (v) => v.incomeSources.includes("SALARY") },
  { kind: "tick", top: 80.13, left: 44.25, on: (v) => v.incomeSources.includes("BUSINESS") },
  { kind: "tick", top: 80.13, left: 67.55, on: (v) => v.incomeSources.includes("HOUSE_PROPERTY") },
  { kind: "tick", top: 82.05, left: 31.38, on: (v) => v.incomeSources.includes("CAPITAL_GAINS") },
  { kind: "tick", top: 82.05, left: 44.29, on: (v) => v.incomeSources.includes("OTHER_SOURCES") },
  { kind: "tick", top: 82.05, left: 67.6, on: (v) => v.incomeSources.includes("NO_INCOME") },
  // PART C — Q12 single-parent Yes/No (boxes y1257-1277)
  { kind: "tick", top: 86.7, left: 41.63, on: (v) => v.singleParent === "YES" },
  { kind: "tick", top: 86.7, left: 51.98, on: (v) => v.singleParent === "NO" },
  // PART C — Father (page 1 of Form 93)
  { kind: "grid", key: "fatherFirst", top: 87.9, left: 30.27, pitch: NEW_PITCH, maxChars: NAME_MAX },
  { kind: "grid", key: "fatherMiddle", top: 89.5, left: 30.27, pitch: NEW_PITCH, maxChars: NAME_MAX },
  { kind: "grid", key: "fatherLast", top: 91.05, left: 30.27, pitch: NEW_PITCH, maxChars: NAME_MAX },

  // ===== PAGE 2 =====
  // PART C continued — §14 Mother's Name. Coordinates measured off the page-2
  // background: box tops at 2.60 / 4.17 / 5.75%, grid left edge 30.08%, pitch
  // 2.58% — then the same +0.15% top offset the calibrated page-1 rows use.
  { kind: "grid", key: "motherFirst", top: 2.75, left: 30.3, pitch: NEW_PITCH, maxChars: NAME_MAX, page: 2 },
  { kind: "grid", key: "motherMiddle", top: 4.32, left: 30.3, pitch: NEW_PITCH, maxChars: NAME_MAX, page: 2 },
  { kind: "grid", key: "motherLast", top: 5.9, left: 30.3, pitch: NEW_PITCH, maxChars: NAME_MAX, page: 2 },
  // §15 — Name of parent to be printed on the PAN card (select one).
  // Boxes x578-605 / 685-712, y112-135.
  { kind: "tick", top: 8.48, left: 57.25, on: (v) => v.printParent === "FATHER", page: 2 },
  { kind: "tick", top: 8.48, left: 67.6, on: (v) => v.printParent === "MOTHER", page: 2 },
  // Part D — Assessing Officer (AO) Code. Measured cell grids:
  //   Area Code  x412-492 (3 cells, 27px), Range Code x413-493 (3 cells)
  //   AO Type    x690-743 (2 cells),       AO No      x690-743 (2 cells)
  // `maxChars` is the PRINTED cell count — anything longer would be drawn past
  // the last box. Row centres are y194 / y217, and grid text is top-anchored,
  // so `top` is the centre minus the calibrated 0.52% glyph half-height.
  { kind: "grid", key: "aoAreaCode", top: 12.75, left: 39.85, pitch: 2.611, maxChars: 3, page: 2 },
  { kind: "grid", key: "aoType", top: 12.75, left: 66.73, pitch: 2.515, maxChars: 2, page: 2 },
  { kind: "grid", key: "aoRangeCode", top: 14.32, left: 39.94, pitch: 2.515, maxChars: 3, page: 2 },
  { kind: "grid", key: "aoNo", top: 14.32, left: 66.73, pitch: 2.515, maxChars: 2, page: 2 },
  // Part E — Representative Assessee name / PAN / Aadhaar. Every row below was
  // re-measured off the page-2 background (the earlier values were estimates
  // and printed a row low and to the right of the boxes).
  { kind: "grid", key: "raFirst", top: 19.62, left: 29.79, pitch: NEW_PITCH, maxChars: NAME_MAX, page: 2 },
  { kind: "grid", key: "raMiddle", top: 21.2, left: 29.79, pitch: NEW_PITCH, maxChars: NAME_MAX, page: 2 },
  { kind: "grid", key: "raLast", top: 22.77, left: 29.79, pitch: NEW_PITCH, maxChars: NAME_MAX, page: 2 },
  { kind: "grid", key: "raPan", top: 24.62, left: 29.88, pitch: 2.582, maxChars: 10, page: 2 },
  { kind: "grid", key: "raAadhaar", top: 26.53, left: 47.2, pitch: 2.579, maxChars: 12, page: 2 },
  // §20 — Representative Assessee address
  { kind: "grid", key: "raFlat", top: 30.29, left: 29.88, pitch: NEW_PITCH, maxChars: NAME_MAX, page: 2 },
  { kind: "grid", key: "raRoad", top: 31.87, left: 29.88, pitch: NEW_PITCH, maxChars: NAME_MAX, page: 2 },
  { kind: "grid", key: "raPostOffice", top: 33.51, left: 29.88, pitch: NEW_PITCH, maxChars: NAME_MAX, page: 2 },
  { kind: "grid", key: "raArea", top: 35.08, left: 29.88, pitch: NEW_PITCH, maxChars: NAME_MAX, page: 2 },
  { kind: "grid", key: "raDistrict", top: 36.66, left: 29.88, pitch: NEW_PITCH, maxChars: NAME_MAX, page: 2 },
  // State box x223-364, Country box x495-656, PIN grid x788-975 (row y556-577)
  { kind: "text", key: "raState", top: 38.49, left: 21.6, width: 13.6, fontSize: 9, uppercase: true, align: "center", page: 2 },
  { kind: "text", key: "raCountry", top: 38.42, left: 47.9, width: 15.6, fontSize: 10, uppercase: true, align: "center", page: 2 },
  { kind: "grid", key: "raPincode", top: 38.23, left: 78.82, pitch: 2.579, maxChars: 6, page: 2 },
  // §21 — Representative Assessee contact (rows y610-630 / y635-655 / y659-679)
  { kind: "grid", key: "raMobileCC", top: 41.92, left: 39.75, pitch: 2.579, maxChars: 3, page: 2 },
  { kind: "grid", key: "raMobile", top: 41.92, left: 59.96, pitch: 2.582, maxChars: 10, page: 2 },
  { kind: "text", key: "raEmail", top: 43.86, left: 40.4, width: 53, fontSize: 10, page: 2 },
  { kind: "grid", key: "raLandlineStd", top: 45.27, left: 39.94, pitch: 2.563, maxChars: 4, page: 2 },
  { kind: "grid", key: "raLandline", top: 45.27, left: 60.25, pitch: 2.502, maxChars: 8, page: 2 },
  // Part F §22 — Address for Communication (select one). Boxes y721-741.
  { kind: "tick", top: 50.03, left: 37.19, on: (v) => v.addressForComm === "RESIDENCE", page: 2 },
  { kind: "tick", top: 50.03, left: 51.6, on: (v) => v.addressForComm === "RA", page: 2 },
  { kind: "tick", top: 50.03, left: 76.11, on: (v) => v.addressForComm === "OFFICE", page: 2 },
  // Part G §23 — Documents submitted by the applicant. The form only has a tick
  // box, so selecting a document from the code master ticks it; the code itself
  // is kept in formData for the vendor upload file. Boxes y820-840.
  { kind: "tick", top: 56.81, left: 12.72, on: (v) => v.proofOfIdentity || !!v.poiCode, page: 2 },
  { kind: "tick", top: 56.81, left: 29.3, on: (v) => v.proofOfAddress || !!v.poaCode, page: 2 },
  { kind: "tick", top: 56.81, left: 46.28, on: (v) => v.proofOfDob || !!v.podbCode, page: 2 },
  // §24 — Documents submitted by the Representative Assessee (boxes y885-904)
  { kind: "tick", top: 61.22, left: 12.72, on: (v) => v.raProofOfIdentity || !!v.raPoiCode, page: 2 },
  { kind: "tick", top: 61.22, left: 29.3, on: (v) => v.raProofOfAddress || !!v.raPoaCode, page: 2 },
  // Verification & Declaration. `top` is the top of the text box, NOT the
  // baseline, so it is derived from the printed baseline with the drop measured
  // in DECL_BASELINE_DROP (see above) — the typed value then rests on the same
  // baseline as the printed label beside it. `left` is 5px past the last dot of
  // each label's leader, so the entry starts on the rule instead of a whole
  // character-width away from it.
  //   "a. I, ..." rule  baseline y986   (typed 1px clear of the rule → y985)
  //
  // Place and Date are the exception: their printed leader is a 1px dotted rule
  // ON the baseline, running x132-166 (Place) and x127-168 (Date) — measured by
  // pixel-scanning the page-2 background. Starting the value past the last dot
  // printed "Place......... demo", the value sitting AFTER the line instead of
  // on it. Each now starts on the leader's FIRST dot and carries its own dotted
  // rule along the same baseline for the full field width, so the value reads
  // as written on the line — the same shape as the "Name:" and "Designation:"
  // rules lower down the page.
  //   "Place........"  baseline y1092, leader x132-166 → left 132/1034 = 12.77%
  //   "Date.........."  baseline y1119, leader x127-168 → left 127/1034 = 12.28%
  { kind: "text", key: "declarantName", top: declTop(985), left: 12.0, width: 23, fontSize: 10, page: 2 },
  { kind: "text", key: "declarantCapacity", top: declTop(985), left: 47.5, width: 11, fontSize: 10, align: "center", page: 2 },
  { kind: "text", key: "place", top: declTop(1092), rule: true, ruleTop: declRuleTop(1092), left: 12.77, width: 22, fontSize: 10, page: 2 },
  { kind: "text", key: "date", top: declTop(1119), rule: true, ruleTop: declRuleTop(1119), left: 12.28, width: 22, fontSize: 10, page: 2 },
  // Signature box measured at x570-920 (55.13%-89.07%), y1135-1244 of the
  // 1034 x 1461 page-2 background — i.e. 55.13%/77.69% to 89.07%/85.15%. The
  // box is now the exact bracket and the signature is centred and clipped
  // inside it (see PanFormTemplate), so it can no longer print outside.
  { kind: "image", key: "signature", top: 77.63, left: 55.13, width: 33.94, height: 7.46, page: 2 },
  // "Name:" rule at y1345, "Designation:" rule at y1392 — the typed value sits
  // 2px clear of each so the letters never merge into the printed line.
  { kind: "text", key: "declarantName", top: declTop(1343), left: 67.0, width: 20, fontSize: 10, page: 2 },
  { kind: "text", key: "designation", top: declTop(1390), left: 70.5, width: 20, fontSize: 10, page: 2 },
];

// ---------------------------------------------------------------------------
// CORRECTION form — Request For Changes Or Correction. Grid left = 29.59
// ---------------------------------------------------------------------------
export const CORRECTION_FORM_FIELDS: PanFormField[] = [
  // Two photo boxes, same as the NEW form. Measured off the page-1 background:
  //   left  box: x 61–233  (5.90%–22.53%), y 40–261 (2.74%–17.85%)
  //   right box: x 800–972 (77.37%–94.00%), y 40–261
  // Both 172 x 221 px = 3.49 x 4.51 cm. (The line at 19.29% is the section
  // header BELOW the boxes, not their bottom edge.)
  { kind: "image", key: "photo", top: 2.74, left: 5.9, width: 16.63, height: 15.12 },
  { kind: "image", key: "photo", top: 2.74, left: 77.37, width: 16.63, height: 15.18 },
  // Same placement as the NEW form, shifted with the left photo (5.9 vs 7.64).
  { kind: "crossSign", key: "signature", top: 11, left: 13.76, width: 16, height: 5.5, rotate: -6 },
  // Top: PAN + Aadhaar (centered)
  { kind: "grid", key: "pan", top: 10.4, left: 38.1, pitch: 2.47, maxChars: 10 },
  { kind: "grid", key: "aadhaar", top: 15.5, left: 35.59, pitch: 2.47, maxChars: 12 },
  // PART A — Name
  { kind: "grid", key: "firstName", top: 24.35, left: 29.59, pitch: CORR_PITCH, maxChars: NAME_MAX },
  { kind: "grid", key: "middleName", top: 25.95, left: 29.59, pitch: CORR_PITCH, maxChars: NAME_MAX },
  { kind: "grid", key: "lastName", top: 27.5, left: 29.59, pitch: CORR_PITCH, maxChars: NAME_MAX },
  // Three-row "B. Name (as per Aadhaar)" block, as on the NEW form. Row
  // interiors measured at y455-475, y478-498, y501-521.
  { kind: "grid", key: "aadhaarName", top: 31.3, left: 29.59, pitch: CORR_PITCH, maxChars: NAME_MAX, line: 0, lines: AADHAAR_NAME_ROWS },
  { kind: "grid", key: "aadhaarName", top: 32.86, left: 29.59, pitch: CORR_PITCH, maxChars: NAME_MAX, line: 1, lines: AADHAAR_NAME_ROWS },
  { kind: "grid", key: "aadhaarName", top: 34.43, left: 29.59, pitch: CORR_PITCH, maxChars: NAME_MAX, line: 2, lines: AADHAAR_NAME_ROWS },
  // Gender ticks — boxes x306-334 / 386-414 / 492-520, y525-545
  { kind: "tick", top: 36.63, left: 31.0, on: (v) => v.gender === "MALE" },
  { kind: "tick", top: 36.63, left: 38.73, on: (v) => v.gender === "FEMALE" },
  { kind: "tick", top: 36.63, left: 48.98, on: (v) => v.gender === "TRANSGENDER" },
  // Date of Birth
  { kind: "grid", key: "dob", top: 37.6, left: 29.59, pitch: 2.55, slice: [0, 2] },
  { kind: "grid", key: "dob", top: 37.6, left: 37.33, pitch: 2.55, slice: [2, 4] },
  { kind: "grid", key: "dob", top: 37.6, left: 45.07, pitch: 2.51, slice: [4, 8] },
  // Address type — "select one": Residence / Office (boxes y606-629)
  { kind: "tick", top: 42.27, left: 31.29, on: (v) => v.addressType === "RESIDENCE" },
  { kind: "tick", top: 42.27, left: 45.07, on: (v) => v.addressType === "OFFICE" },
  // Address
  { kind: "grid", key: "flat", top: 43.4, left: 29.59, pitch: CORR_PITCH, maxChars: NAME_MAX },
  { kind: "grid", key: "road", top: 44.95, left: 29.59, pitch: CORR_PITCH, maxChars: NAME_MAX },
  { kind: "grid", key: "postOffice", top: 46.6, left: 29.59, pitch: CORR_PITCH, maxChars: NAME_MAX },
  { kind: "grid", key: "area", top: 48.2, left: 29.59, pitch: CORR_PITCH, maxChars: NAME_MAX },
  { kind: "grid", key: "district", top: 49.75, left: 29.59, pitch: CORR_PITCH, maxChars: NAME_MAX },
  { kind: "text", key: "state", top: 51.55, left: 23.0, width: 13, fontSize: 10, uppercase: true, align: "center" },
  { kind: "text", key: "country", top: 51.55, left: 49.3, width: 15, fontSize: 10, uppercase: true, align: "center" },
  { kind: "grid", key: "pincode", top: 51.55, left: 76.0, pitch: 2.58, maxChars: 6 },
  // Contact (Mobile Number row, not the Landline row below it)
  { kind: "grid", key: "mobileCountryCode", top: 60.5, left: 46.81, pitch: 2.5, maxChars: 3 },
  { kind: "grid", key: "mobile", top: 60.5, left: 69.34, pitch: 2.47, maxChars: 10 },
  // Email sits in the wide box to the right of the label — measured x484-972
  // (46.81%-94.0%), y905-925, so the value is centred in the box rather than
  // printed on its top border.
  { kind: "text", key: "email", top: 62.33, left: 47.4, width: 46, fontSize: 10 },
  // PART B — Parents
  { kind: "grid", key: "fatherFirst", top: 71.9, left: 29.59, pitch: CORR_PITCH, maxChars: NAME_MAX },
  { kind: "grid", key: "fatherMiddle", top: 73.5, left: 29.59, pitch: CORR_PITCH, maxChars: NAME_MAX },
  { kind: "grid", key: "fatherLast", top: 75.05, left: 29.59, pitch: CORR_PITCH, maxChars: NAME_MAX },
  { kind: "grid", key: "motherFirst", top: 76.7, left: 29.59, pitch: CORR_PITCH, maxChars: NAME_MAX },
  { kind: "grid", key: "motherMiddle", top: 78.3, left: 29.59, pitch: CORR_PITCH, maxChars: NAME_MAX },
  { kind: "grid", key: "motherLast", top: 79.85, left: 29.59, pitch: CORR_PITCH, maxChars: NAME_MAX },
  // Q10 — Name of parent to be printed on the PAN card (boxes y1192-1215)
  { kind: "tick", top: 82.35, left: 58.95, on: (v) => v.printParent === "FATHER" },
  { kind: "tick", top: 82.35, left: 70.45, on: (v) => v.printParent === "MOTHER" },

  // Left "Tick Box" column (Sr. No. 1–9) — which fields are being changed.
  // Sr. 1 / 3 / 7 / 8 are ticked by default (see PanFormDialog init). Every
  // cell in this column is x128-157 (x131-159 for the last two rows).
  { kind: "tick", top: 23.22, left: 13.83, on: (v) => v.changeName },
  { kind: "tick", top: 36.63, left: 13.83, on: (v) => v.changeGender },
  { kind: "tick", top: 38.2, left: 13.83, on: (v) => v.changeDob },
  { kind: "tick", top: 40.22, left: 13.83, on: (v) => v.changeAddress },
  { kind: "tick", top: 53.52, left: 13.83, on: (v) => v.changePassport },
  { kind: "tick", top: 55.1, left: 13.83, on: (v) => v.changeTin },
  { kind: "tick", top: 58.69, left: 13.83, on: (v) => v.changeContact },
  { kind: "tick", top: 72.4, left: 14.07, on: (v) => v.changeFather },
  { kind: "tick", top: 77.19, left: 14.07, on: (v) => v.changeMother },

  // Part C §11 — Documents submitted as proof. As on the NEW form, picking a
  // document from the code master ticks the box. Rows y1300-1320 / y1341-1361.
  { kind: "tick", top: 89.64, left: 12.72, on: (v) => v.corrProofOfIdentity || !!v.poiCode },
  { kind: "tick", top: 89.64, left: 29.3, on: (v) => v.corrProofOfAddress || !!v.poaCode },
  { kind: "tick", top: 89.64, left: 46.28, on: (v) => v.corrProofOfDob || !!v.podbCode },
  { kind: "tick", top: 92.44, left: 12.72, on: (v) => v.corrDocProof },
  { kind: "tick", top: 92.44, left: 46.23, on: (v) => v.corrCopyOfPan },

  // ===== PAGE 2 — Verification & Declaration (no Name/Designation on this form) =====
  // Same baseline rule as the NEW form, measured off this page's own pixels.
  //   "a. I , ..." rule baseline y104 (typed 1px clear of the rule → y103)
  //   "Place........"  baseline y174, leader x132-166 → left 12.77%
  //   "Date.........." baseline y217, leader x127-168 → left 12.28%
  // Place / Date start on the leader's first dot with their own dotted rule,
  // as on the NEW form — starting past the last dot left a wide gap.
  { kind: "text", key: "declarantName", top: declTop(103), left: 11.6, width: 24, fontSize: 10, page: 2 },
  { kind: "text", key: "declarantCapacity", top: declTop(103), left: 47.5, width: 10.5, fontSize: 10, align: "center", page: 2 },
  { kind: "text", key: "place", top: declTop(174), rule: true, ruleTop: declRuleTop(174), left: 12.77, width: 22, fontSize: 10, page: 2 },
  { kind: "text", key: "date", top: declTop(217), rule: true, ruleTop: declRuleTop(217), left: 12.28, width: 22, fontSize: 10, page: 2 },
  // Signature box measured at x570-920 (55.13%-89.07%), y246-353 — i.e.
  // 55.13%/16.84% to 89.07%/24.16%. Exact bracket, same as the NEW form.
  { kind: "image", key: "signature", top: 16.83, left: 55.13, width: 33.94, height: 7.32, page: 2 },
];

/**
 * Height of the form's pinned action bar, in pixels.
 *
 * The bar holds the step chips and Close / Next, and is pinned to the top of
 * the page (PanFormDialog). The photo / signature strip at the head of step 1
 * pins itself directly below it (PanFormStepOne), so the two need one number
 * between them — the strip's `top` is this, and a bar left to size itself to
 * its buttons would slide the strip underneath it.
 */
export const ACTION_BAR_HEIGHT = 56;
